<script setup lang="ts">
import { useI18n } from '@/i18n';
import { useBillingStore } from '@/stores/billing';
import { computed, onMounted, ref } from 'vue';
import type { CurrentSubscriptionInfo, Offerings, PlanProduct } from '@/types/billing';
import { LayoutGap, Prose } from '@dabble/quillui';
import Plan from './components/Plan.vue';

// Composables
const billingStore = useBillingStore();
const { t } = useI18n();

// State
const selection = ref<'month' | 'year'>('month'); // Default to monthly

// Computed properties from store
const offerings = computed(() => billingStore.offerings as Offerings | null);

// Determine current plan details from the primary subscription (same logic as billing portal)
const currentPlanDetails = computed<CurrentSubscriptionInfo | null>(() => {
  const sub = billingStore.primarySubscription;
  if (!sub) return null;

  const productId = sub.plan?.product?.id;

  return {
    id: sub.plan?.id ?? '',
    product: productId ? { id: productId, name: sub.plan?.product?.name ?? '' } : undefined,
    interval: sub.plan?.interval ?? 'month',
    status: sub.status,
    metadata: sub.metadata,
    offerings: billingStore.offerings ?? undefined,
  };
});

const isTrialing = computed(() => billingStore.isTrialing);

// Initialize selection based on current plan interval
onMounted(() => {
  if (currentPlanDetails.value?.interval === 'year') {
    selection.value = 'year';
  }
  if (!offerings.value && !billingStore.isLoading) {
    billingStore.fetchBillingOverview();
  }
});

// Sorting logic for plans
function getPrice(
  product: PlanProduct | undefined,
  interval: 'month' | 'year' | 'one-time',
): number | 'lifetime' | null {
  if (!product) return null;
  if (interval === 'one-time' && product['one-time']?.amount !== undefined) return 'lifetime'; // Treat one-time as lifetime for sorting
  return product[interval]?.amount ?? null;
}

const orderedProductIDs = computed(() => {
  const plansData = offerings.value?.plans?.plans;
  if (!plansData) return [];

  return Object.keys(plansData).sort((aKey, bKey) => {
    const productA = plansData[aKey];
    const productB = plansData[bKey];

    // Use monthly price for sorting, treat one-time as highest
    const priceA = getPrice(productA, 'one-time') ?? getPrice(productA, 'month');
    const priceB = getPrice(productB, 'one-time') ?? getPrice(productB, 'month');

    if (priceA === priceB) return 0;
    if (priceA === 'lifetime') return 1; // Lifetime is highest
    if (priceB === 'lifetime') return -1;
    if (priceA === null) return 1; // Plans without price go last
    if (priceB === null) return -1;

    return priceA < priceB ? -1 : 1;
  });
});

// Method to toggle selection
function toggleSelection() {
  selection.value = selection.value === 'month' ? 'year' : 'month';
}

// Method to determine if a plan is recommended (e.g., 'standard')
function isRecommended(prodID: string): boolean {
  const product = offerings.value?.plans?.plans?.[prodID];
  return product?.name?.toLowerCase() === 'standard';
}
</script>

<template>
  <div class="billing-container">
    <!-- Interval Selector: custom pill toggle keeps bespoke SCSS (see plan exceptions). -->
    <LayoutGap direction="column" align="center" size="small" class="interval-selector">
      <Prose size="small" variant="secondary" class="interval-discount">
        {{ t('checkout_pay_annually') }}
      </Prose>
      <button
        type="button"
        class="toggle top"
        :class="{ yearly: selection === 'year' }"
        @click="toggleSelection"
        :aria-label="t('checkout_toggle_interval_aria')"
      >
        <div class="highlight" />
        <div class="label" :class="{ selected: selection === 'month' }">
          {{ t('checkout_interval_monthly') }}
        </div>
        <div class="label" :class="{ selected: selection === 'year' }">
          {{ t('checkout_interval_annually') }}
        </div>
      </button>
    </LayoutGap>

    <LayoutGap
      v-if="offerings?.plans?.plans"
      direction="row"
      align="center"
      vertical-align="stretch"
      wrap
      size="large"
      class="plan-list"
    >
      <Plan
        v-for="prodID in orderedProductIDs"
        :key="prodID"
        :product="offerings.plans.plans[prodID]"
        :productID="prodID"
        :current="currentPlanDetails"
        :isTrialing="isTrialing"
        :selection="selection"
        :offerings="offerings"
        :recommend="isRecommended(prodID)"
      />
      <!-- TODO: Add Legacy Plan display logic if needed -->
      <!-- <Plan v-if="isLegacy" :isLegacy="true" :current="currentPlanDetails" ... /> -->
    </LayoutGap>
    <div v-else-if="billingStore.isLoading">
      <div class="loading-plans">{{ t('billing_plans_loading') }}</div>
    </div>
    <div v-else>
      <p class="no-plans">{{ t('billing_plans_load_error') }}</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Using SCSS with CSS variables and some direct styles
.billing-container {
  margin-left: auto;
  margin-right: auto;
  padding: 0 1rem; // Add padding
  max-width: 70rem;
}

.interval-selector {
  position: relative;
  margin: 20px auto;
}

.interval-discount {
  text-transform: uppercase;
}

.toggle {
  position: relative;
  display: flex;
  width: 240px;
  border-radius: 20px;
  padding: 0;
  background-color: var(--background-alt, #f3f4f6); // Background for the toggle base
  border: none;
  text-transform: inherit;
  font-size: inherit;
  vertical-align: baseline;
  line-height: inherit;
  text-decoration: none;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  overflow: hidden; // Keep highlight contained

  &:hover {
    text-decoration: none;
  }

  .label {
    position: relative; // Keep labels above highlight
    z-index: 1;
    width: 120px;
    padding: 6px 0;
    color: var(--text-color-lighter, #4b5563);
    text-align: center;
    text-transform: uppercase;
    font-size: var(--font-size-xs, 0.75rem);
    font-weight: 500;
    transition: color 150ms ease-out;

    &.selected {
      color: white !important; // Ensure selected text is white
    }
  }

  .highlight {
    position: absolute;
    z-index: 0;
    width: 120px;
    height: 100%;
    top: 0;
    left: 0;
    background: var(--dabble-blue-gradient, linear-gradient(to right, #3b82f6, #2563eb)); // Example gradient
    border-radius: 20px;
    transition: transform 150ms ease-out;
  }

  // Move highlight when yearly is selected
  &.yearly .highlight {
    transform: translateX(100%);
  }
}

.plan-list {
  margin-top: 4rem;
  padding-bottom: 2rem;
}

.loading-plans,
.no-plans {
  text-align: center;
  margin-top: 4rem;
  color: var(--text-color-light, #4b5563);
}
</style>
